import { useCallback, useEffect, useRef, useState } from 'react';
import { MapMouseEvent } from 'mapbox-gl';
import { useIntl } from 'react-intl';
import { MarkerProperties } from '../../../types/CustomRoute';
import { useDispatch, useSelector } from '../../../redux';
import { updateCursor } from '../../../redux/reducer';
import { updateActiveSegmentHandle, selectCustomRouteActiveSegment, setToastMessage } from '../../../redux/customRoute';
import useRerouteMarker from './useRerouteMarker';
import { validateProximityToPreviousPoint, getPointForHandle } from './validationUtils';

export const CURSOR_DRAGGING = 'grabbing';

const useDragMarker = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const isSmartRouting = useSelector(state => state.customRoute.isSmartRouting);
  const activeSegment = useSelector(selectCustomRouteActiveSegment);

  // State that should not trigger a re-render
  const isStartingRef = useRef(false);
  const isPendingRef = useRef(false);
  const startingMarkerRef = useRef<MarkerProperties | undefined>(undefined);

  // State that should trigger a re-render
  const [pendingMarker, setPendingMarker] = useState<MarkerProperties | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  const rerouteMarker = useRerouteMarker();

  const start = useCallback(
    (event: MapMouseEvent) => {
      const feature = event.features?.[0];

      if (!feature) {
        return;
      }

      isStartingRef.current = true;
      // Track the marker the mousedown event occurred on but don't set the dragging marker until the mousemove event
      startingMarkerRef.current = feature.properties as MarkerProperties;
      dispatch(updateCursor(CURSOR_DRAGGING));

      // Prevent default behavior to avoid map panning
      event.preventDefault();
    },
    [dispatch]
  );

  // Update dragging state and move the marker if dragging conditions are met
  const update = useCallback((event: MapMouseEvent) => {
    if (isStartingRef.current) {
      isPendingRef.current = true;
      isStartingRef.current = false;
    }

    if (isPendingRef.current) {
      const {
        lngLat: { lng, lat }
      } = event;
      setPendingMarker({ ...startingMarkerRef.current!, latitude: lat, longitude: lng });
    }
  }, []);

  // Save the moved marker to the redux store when the user is done dragging
  // We use a flag to trigger saving instead of doing it in the callback to prevent re-registering event handlers
  // Keep the dependency array empty to prevent re-registering event handlers
  const save = useCallback(() => {
    isStartingRef.current = false;
    isPendingRef.current = false;
    startingMarkerRef.current = undefined;
    setIsSaving(true);
  }, []);

  // Save changes when dragging a marker is done
  useEffect(() => {
    if (!isSaving) {
      return;
    }

    const perform = async () => {
      if (pendingMarker) {
        if (isSmartRouting) {
          await rerouteMarker(pendingMarker);
        } else {
          const newPoint: [number, number] = [pendingMarker.latitude, pendingMarker.longitude];
          let prevPoint: [number, number] | undefined = undefined;
          if (activeSegment && pendingMarker) {
            const { isStart, isEnd, pathIndex: rawPathIndex, endPathIndex } = pendingMarker;
            const selectedPathIndex = isStart && isEnd ? endPathIndex! : rawPathIndex;
            const handleIndex = isEnd ? selectedPathIndex + 1 : selectedPathIndex;
            prevPoint = getPointForHandle(activeSegment, handleIndex);
          }
          if (prevPoint && !validateProximityToPreviousPoint(newPoint, prevPoint)) {
            dispatch(setToastMessage({ iconCode: 'error', message: intl.formatMessage({ defaultMessage: 'Tap or draw closer to your route' }) }));
            setIsSaving(false);
            setPendingMarker(undefined);
            return;
          }

          dispatch(updateActiveSegmentHandle({ ...pendingMarker }));
        }
      }

      // Clear the save flag and the dragging marker once it is saved
      setIsSaving(false);
      setPendingMarker(undefined);
    };

    // Run async so that we don't clear dragging marker state before the route is updated
    perform();
  }, [dispatch, pendingMarker, isSaving, isSmartRouting, rerouteMarker, activeSegment, intl]);

  return {
    isStartingRef,
    isPendingRef,
    pendingMarker,
    start,
    update,
    save
  };
};

export default useDragMarker;
